/**
 * The cart, kept on the device between page loads.
 *
 * It used to live in memory only, so anything that reloaded the till emptied it: a refresh,
 * Android discarding the tab while the salesperson checked an M-Pesa SMS, the PIN screen after
 * the session lapsed. A tester went to My cash and back and found no Clear case in the cart.
 *
 * Saved per person and per shop, and only for a working day: a cart is somebody's sale in
 * progress, not something the next person to pick up the phone inherits. Signing out wipes it
 * with the rest of local state (`wipeLocalState`), so there is nothing extra to clear there.
 */

import type { CartState } from './cartSlice.js';

const KEY = 'pos.cart';
const VERSION = 1;
const KEEP_MS = 12 * 60 * 60 * 1000;

type Saved = { v: number; owner: string; savedAt: number; cart: CartState };

export type CartOwner = { userId: string; shopId: string };

const ownerKey = (owner: CartOwner) => `${owner.userId}:${owner.shopId}`;

export function isEmptyCart(cart: CartState): boolean {
  return cart.lines.length === 0 && cart.genericLines.length === 0 && cart.saleId === null;
}

export function saveCart(owner: CartOwner, cart: CartState): void {
  try {
    if (isEmptyCart(cart)) {
      localStorage.removeItem(KEY);
      return;
    }

    const saved: Saved = { v: VERSION, owner: ownerKey(owner), savedAt: Date.now(), cart };
    localStorage.setItem(KEY, JSON.stringify(saved));
  } catch {
    // Private mode or full storage: the cart still works, it just does not survive a reload.
  }
}

/** The saved cart, if it is this person's, at this shop, and from today. */
export function loadCart(owner: CartOwner): CartState | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return null;

    const saved = JSON.parse(raw) as Partial<Saved>;
    const cart = saved.cart;
    const usable =
      saved.v === VERSION &&
      saved.owner === ownerKey(owner) &&
      typeof saved.savedAt === 'number' &&
      Date.now() - saved.savedAt < KEEP_MS;

    if (!usable || cart === undefined) {
      localStorage.removeItem(KEY);
      return null;
    }

    // Whatever was in flight when the page went away did not finish here; the idempotency key
    // is kept, so a retry is still the same sale to the server.
    return { ...cart, submission: 'idle', lastError: null };
  } catch {
    return null;
  }
}

/**
 * How many items this person has waiting in a saved cart, without touching it — for the sign-in
 * and PIN screens to say "your sale is saved" before anyone is signed in. Any shop: the screen
 * does not know the shop yet, and the cart itself is only restored for the right one.
 */
export function savedItemCount(userId: string): number {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return 0;

    const saved = JSON.parse(raw) as Partial<Saved>;
    if (
      saved.v !== VERSION ||
      typeof saved.owner !== 'string' ||
      !saved.owner.startsWith(`${userId}:`) ||
      typeof saved.savedAt !== 'number' ||
      Date.now() - saved.savedAt >= KEEP_MS ||
      saved.cart === undefined
    ) {
      return 0;
    }

    return (
      saved.cart.lines.reduce((sum, line) => sum + line.quantity, 0) +
      saved.cart.genericLines.reduce((sum, line) => sum + line.quantity, 0)
    );
  } catch {
    return 0;
  }
}
